// Which app version each user is running, per platform (#141 follow-up,
// modelled on meditation_group's clientInfoByPlatform). Written to
// users/{uid}.clientInfoByPlatform.<platform> on every launch/login, so a user
// on both iOS and web keeps a record for each. Pure — the caller passes the
// live expo-constants / expo-updates values, so this unit-tests without native
// modules.

export type ClientPlatform = "ios" | "android" | "web";

const PLATFORM_ORDER: ClientPlatform[] = ["ios", "android", "web"];
const PLATFORM_LABEL: Record<ClientPlatform, string> = {
  ios: "iOS",
  android: "Android",
  web: "Web",
};

export interface ClientInfo {
  platform: ClientPlatform;
  osVersion?: string | null;
  appVersion: string | null; // app.json version baked into the bundle
  runtimeVersion?: string | null; // OTA compatibility key; null on web
  channel?: string | null; // EAS Update channel; null on web
  otaUpdateId?: string | null; // running OTA; null on the store bundle / web
  otaPublishedAt?: number | null; // epoch ms the running OTA was published
}

export interface StoredClientInfo extends ClientInfo {
  updatedAt: number | null; // epoch ms this platform last reported
}

export interface ClientInfoInput {
  platform: ClientPlatform;
  osVersion?: string | null;
  appVersion?: string | null; // Constants.expoConfig?.version
  isUpdatesEnabled: boolean; // Updates.isEnabled
  runtimeVersion?: string | null; // Updates.runtimeVersion
  channel?: string | null; // Updates.channel
  updateId?: string | null; // Updates.updateId
  updateCreatedAt?: Date | null; // Updates.createdAt
  isEmbeddedLaunch?: boolean; // Updates.isEmbeddedLaunch
}

export function buildClientInfo(input: ClientInfoInput): ClientInfo {
  // expo-updates' web shim claims isEnabled with empty-string values, so never
  // trust it on web, and store "" as null.
  const updates = input.isUpdatesEnabled && input.platform !== "web";
  const onOta = updates && input.isEmbeddedLaunch === false;
  return {
    platform: input.platform,
    osVersion: input.osVersion || null,
    appVersion: input.appVersion || null,
    runtimeVersion: updates ? input.runtimeVersion || null : null,
    channel: updates ? input.channel || null : null,
    otaUpdateId: onOta ? input.updateId || null : null,
    otaPublishedAt: onOta ? (input.updateCreatedAt?.getTime() ?? null) : null,
  };
}

function toMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number") return value;
  const ts = value as { toMillis?: () => number; seconds?: number };
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  return null;
}

/** Reads a user doc's clientInfoByPlatform map into ios/android/web order. */
export function parseClientInfoByPlatform(raw: unknown): StoredClientInfo[] {
  if (!raw || typeof raw !== "object") return [];
  const map = raw as Record<string, Record<string, unknown> | undefined>;
  return PLATFORM_ORDER.flatMap((platform) => {
    const entry = map[platform];
    if (!entry || typeof entry !== "object") return [];
    const str = (key: string) =>
      typeof entry[key] === "string" ? (entry[key] as string) : null;
    return [
      {
        platform,
        osVersion: str("osVersion"),
        appVersion: str("appVersion"),
        runtimeVersion: str("runtimeVersion"),
        channel: str("channel"),
        otaUpdateId: str("otaUpdateId"),
        otaPublishedAt: toMillis(entry.otaPublishedAt),
        updatedAt: toMillis(entry.updatedAt),
      },
    ];
  });
}

/** One-line summary, e.g. "iOS 1.0.10 · OTA 5afe474b". */
export function formatClientInfo(info: StoredClientInfo): string {
  const parts = [
    `${PLATFORM_LABEL[info.platform]} ${info.appVersion ?? "unknown"}`,
  ];
  if (info.platform !== "web") {
    parts.push(
      info.otaUpdateId ? `OTA ${info.otaUpdateId.slice(0, 8)}` : "store build",
    );
  }
  return parts.join(" · ");
}
